import { Cmd, Loop, loop, LoopReducer } from "redux-loop";
import {
  ActionType,
  createAction,
  createAsyncAction,
  getType,
} from "typesafe-actions";
import { getParticipantDetails } from "../events/eventReducer";
import eventService from "../services/events";
import { historyService } from "../services/historyService";
import { formInputInfo, ParticipantState } from "../types/types";
import {
  convertParticipantToListFormat,
  convertParticipantToPartypants,
} from "../utils/functions";
import ConsoleHelper from "../utils/logger";

const initialState: ParticipantState = {
  initLoading: false,
  subEventId: null,
  participantUuid: null,
  isPartOfGroup: false,
  groupCommonAnswer: [],
  groupMemberUuids: [],
  participants: [],
  participantsFormatted: [],
};

export const getParticipants = createAsyncAction(
  "GET_EVENT_PARTICIPANTS",
  "GET_PARTICIPANTS_FETCH_COMPLETE",
  "GET_PARTICIPANT_FETCH_FAIL"
)<{ id: string; name: string | null }, { data: any; status: number }, Error>();

export const saveParticipantChanges = createAsyncAction(
  "START_PARTICIPANT_PATCH_REQUEST",
  "PARTICIPANT_PATCH_COMPLETE",
  "PARTICIPANT_PATCH_FAIL"
)<
  { uuid: string; eventId: string },
  { data: any; status: number; eventId: string },
  Error
>();

const changeParticipantsData = createAction("CHANGE_PARTICIPANTS_DATA")<{
  id: string;
  value: string;
  array: boolean;
}>();

type Action =
  | ActionType<typeof getParticipantDetails>
  | ActionType<typeof getParticipants>
  | ActionType<typeof saveParticipantChanges>
  | ActionType<typeof changeParticipantsData>;

export const participantReducer: LoopReducer<ParticipantState, Action> = (
  state: ParticipantState = initialState,
  action: Action
): ParticipantState | Loop<ParticipantState> => {
  switch (action.type) {
    case getType(getParticipantDetails.request):
      return {
        ...state,
        initLoading: true,
      };

    case getType(getParticipantDetails.success):
      return loop(
        {
          ...state,
          initLoading: false,
          subEventId: action.payload.data.subEventId,
          participantUuid: action.payload.data.participantUuid,
          isPartOfGroup: action.payload.data.isPartOfGroup,
          groupCommonAnswer: action.payload.data.groupCommonAnswer,
          groupMemberUuids: action.payload.data.groupMemberUuids,
        },
        Cmd.run(historyService.goto, {
          args: [
            `/events/${action.payload.eventId}/participants/${action.payload.data.participantUuid}`,
          ],
        })
      );

    case getType(getParticipantDetails.failure):
      return {
        ...initialState,
        initLoading: false,
        participants: state.participants,
        participantsFormatted: state.participantsFormatted,
      };

    case getType(getParticipants.request):
      ConsoleHelper("in reducer start of loop");
      return loop(
        state,
        Cmd.run(eventService.getParticipants, {
          successActionCreator: getParticipants.success,
          failActionCreator: getParticipants.failure,
          args: [action.payload.id],
        })
      );
    case getType(getParticipants.failure):
      return { ...state, initLoading: false };

    case getType(getParticipants.success):
      ConsoleHelper("in reducer success, got participants succesfully!");
      const participants = action.payload.data;
      const participantsFormatted = participants.length
        ? participants.map((participant) => {
            return convertParticipantToListFormat(participant);
          })
        : [];
      ConsoleHelper(action.payload);
      return {
        ...state,
        initLoading: false,
        participants,
        participantsFormatted,
      };

    case getType(saveParticipantChanges.request):
      ConsoleHelper("in reducer start of loop");
      const updated = state.participants.find(
        (p) => p.partypantsUuid === action.payload.uuid
      );
      const {
        modifyToken,
        cancelToken,
        partypantsQrCode,
        partypantsRegisterTime,
        ...rest
      } = updated;
      return loop(
        { ...state, initLoading: true },
        Cmd.run(eventService.saveParticipantChanges, {
          successActionCreator: saveParticipantChanges.success,
          failActionCreator: saveParticipantChanges.failure,
          args: [rest, action.payload.eventId],
        })
      );
    case getType(saveParticipantChanges.failure):
      ConsoleHelper("failed to patch", action.payload);
      return { ...state, initLoading: false };

    case getType(saveParticipantChanges.success):
      ConsoleHelper("in reducer success, patched participant succesfully!");
      return loop(
        { ...state, initLoading: false },
        Cmd.action({
          type: "GET_EVENT_PARTICIPANTS",
          payload: { id: action.payload.eventId },
        })
      );

    case getType(changeParticipantsData):
      const participantFormatted = state.participantsFormatted.find(
        (p) => p.uuid === state.participantUuid
      );
      const currentValue = participantFormatted[action.payload.id];
      const result =
        action.payload.array && typeof currentValue === "object"
          ? currentValue.length === 0
            ? [action.payload.value]
            : currentValue.includes(action.payload.value)
            ? currentValue.filter((val) => val !== action.payload.value)
            : currentValue.concat(action.payload.value)
          : action.payload.value;
      const updatedParticipantFormatted = {
        ...participantFormatted,
        [action.payload.id]: result,
      };
      const participantsFormattedUpdated = state.participantsFormatted.map(
        (p) => {
          return p.uuid === state.participantUuid
            ? updatedParticipantFormatted
            : p;
        }
      );

      const participant = state.participants.find(
        (a) => a.partypantsUuid === state.participantUuid
      );
      const updatedParticipant = convertParticipantToPartypants(
        updatedParticipantFormatted,
        participant
      );
      const participantsUpdated = state.participants.map((p) => {
        return p.partypantsUuid === state.participantUuid
          ? updatedParticipant
          : p;
      });
      return {
        ...state,
        participantsFormatted: participantsFormattedUpdated,
        participants: participantsUpdated,
      };

    default:
      return state;
  }
};

export type ParticipantDetailsFormData = {
  inputs: formInputInfo[];
  values: { [key: string]: string | string[] };
  uuid: string;
  initLoading: boolean;
  eventId: string;
};
export function selectParticipantDetailsFormData(
  state: any
): ParticipantDetailsFormData {
  const items = state.eventState.registrationState.registrationQuestions.items;
  const foundParticipant = state.participantState.participantsFormatted.find(
    (participant) => participant.uuid === state.participantState.participantUuid
  );
  const foundIds = foundParticipant ? Object.keys(foundParticipant) : [];
  const inputs = foundIds.map(
    (id) =>
      items.find((item) => item.id === id) || {
        id: id,
        question: "",
        show: true,
        type: "text",
        group: 1,
        page: 1,
      }
  );

  const values = foundParticipant ? foundParticipant : {};
  const uuid = state.participantState.participantUuid;
  const initLoading = state.participantState.initLoading;
  const eventId = state.eventState.eventId;
  return { inputs, values, uuid, initLoading, eventId };
}
